import { useInfiniteQuery } from '@tanstack/react-query'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { getVehiclesWithLocksList } from './getVehiclesWithLocksList'
import {
  PaginatedVehiclesResponse,
  Vehicle,
} from '../vehicle-list/types'

type Props = {
  companyId: string
  pageSize?: number
  groupId?: number
  isActive?: boolean
  search?: string
  enabled?: boolean
}

/**
 * Paginated vehicles-with-locks list. Same pattern as
 * `useInfiniteQueryVehiclesList`, pointed at the locks endpoint.
 */
export function useInfiniteQueryVehiclesWithLocksList({
  companyId,
  pageSize = 20,
  groupId,
  isActive,
  search,
  enabled = true,
}: Props) {
  const query = useInfiniteQuery<PaginatedVehiclesResponse>({
    queryKey: [
      'vehiclesWithLocks-infinite',
      companyId,
      pageSize,
      groupId,
      isActive,
      search,
    ],
    enabled: enabled && !!companyId,
    initialPageParam: 1,
    staleTime: MINUTE_IN_MS * 5,
    queryFn: async ({ pageParam }) => {
      const res = await getVehiclesWithLocksList({
        companyId,
        page: pageParam as number,
        pageSize,
        groupId,
        isActive,
        search,
      })
      return res.data as PaginatedVehiclesResponse
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (sum, p) => sum + (p.vehicles?.length ?? 0),
        0,
      )
      return loaded < (lastPage.totalCount ?? 0) ? allPages.length + 1 : undefined
    },
  })

  const vehicles: Vehicle[] =
    query.data?.pages.flatMap((p) => p.vehicles ?? []) ?? []
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0

  return { ...query, vehicles, totalCount }
}
